// src/components/ClientLogin.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff } from "react-icons/fi";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";
import { toast } from 'react-toastify';

const ClientLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const autoLoginAttempted = useRef(false);
    
    const [formData, setFormData] = useState({
        email: "",
        password: ""
    });
    const [otpData, setOtpData] = useState({
        otp: "",
        clientId: "",
        email: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [showOtpScreen, setShowOtpScreen] = useState(false);
    const [skipOtp, setSkipOtp] = useState(false);
    const [isAutoLogin, setIsAutoLogin] = useState(false);
    const [loginMode, setLoginMode] = useState('client'); // 'client' or 'access'

    // ─── CLEAR ALL TOASTS ───
    const clearAllToasts = () => {
        toast.dismiss();
    };

    // ─── SHOW TOAST WITH CLEANUP ───
    const showToastWithCleanup = (message, type = 'info') => {
        toast[type](message, {
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    };

    // ─── CLEANUP TOASTS ON UNMOUNT ───
    useEffect(() => {
        return () => {
            clearAllToasts();
        };
    }, []);

    // ─── CLEAR TOASTS BEFORE NAVIGATION ───
    const navigateWithCleanup = (path) => {
        clearAllToasts();
        navigate(path);
    };

    // ─── AUTO-LOGIN FROM URL PARAMS ───
    const handleAutoLogin = async (email, password, skipOtpParam) => {
        console.log('🚀 Auto-login triggered for Client:', { email, password, skipOtp: skipOtpParam });
        
        if (autoLoginAttempted.current) {
            console.log('⚠️ Auto-login already attempted, skipping...');
            return;
        }
        autoLoginAttempted.current = true;
        
        clearAllToasts();
        setLoading(true);
        setIsAutoLogin(true);
        setFormData({ email, password });
        setSkipOtp(skipOtpParam === 'true');

        try {
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/clientlogin`, { email, password });
            
            console.log("Login response:", res.data);
            
            if (res.data.success) {
                const clientId = res.data.clientId || "";
                const clientEmail = res.data.email || email;
                
                setOtpData({
                    otp: "",
                    clientId: clientId,
                    email: clientEmail
                });
                
                if (skipOtpParam === 'true') {
                    showToastWithCleanup("🔄 Auto-verifying...", 'success');
                    await handleAutoVerify(clientId, clientEmail);
                } else {
                    setShowOtpScreen(true);
                    showToastWithCleanup("OTP sent to your email! Please verify to continue.", 'success');
                    setLoading(false);
                }
            } else {
                const errorMsg = res.data.message || "Login failed. Please try again.";
                showToastWithCleanup(errorMsg, 'error');
                setLoading(false);
                autoLoginAttempted.current = false;
            }
        } catch (err) {
            console.error("Login error:", err);
            const errorMsg = err.response?.data?.message || "Invalid credentials. Please try again.";
            showToastWithCleanup(errorMsg, 'error');
            setLoading(false);
            autoLoginAttempted.current = false;
        }
    };

    // ─── AUTO-VERIFY OTP (Skip OTP) ───
    const handleAutoVerify = async (clientId, email) => {
        try {
            const payload = {
                clientId: clientId,
                email: email,
                autoLogin: true
            };
            
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/auto-login`, payload);
            
            console.log("Auto-verify response:", res.data);
            
            if (res.data.success && res.data.token && res.data.client) {
                clearAllToasts();
                
                localStorage.setItem("clientToken", res.data.token);
                localStorage.setItem("clientId", res.data.client._id);
                localStorage.setItem("clientCustomId", res.data.client.clientId);
                localStorage.setItem("clientName", res.data.client.name);
                localStorage.setItem("clientEmail", res.data.client.email);
                localStorage.setItem("clientMobile", res.data.client.mobile);
                localStorage.setItem("companyName", res.data.client.companyName);
                localStorage.setItem("employeesCount", res.data.client.employeesCount);
                localStorage.setItem("accessibleProducts", JSON.stringify(res.data.client.accessibleProducts));
                localStorage.setItem("userRole", "client");
                localStorage.setItem("clientData", JSON.stringify(res.data.client));

                showToastWithCleanup("✅ Auto-login successful! Redirecting...", 'success');
                setLoading(false);
                
                setTimeout(() => {
                    navigateWithCleanup("/recruitment-dashboard");
                }, 1000);
            } else {
                await handleFallbackOtp(clientId, email);
            }
        } catch (err) {
            console.error("Auto-verify error:", err);
            await handleFallbackOtp(clientId, email);
        }
    };

    // ─── FALLBACK: Try OTP with dummy OTP ───
    const handleFallbackOtp = async (clientId, email) => {
        try {
            const payload = {
                clientId: clientId,
                otp: "000000"
            };
            
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/verify-client-otp`, payload);
            
            if (res.data.success && res.data.token && res.data.client) {
                clearAllToasts();
                
                localStorage.setItem("clientToken", res.data.token);
                localStorage.setItem("clientId", res.data.client._id);
                localStorage.setItem("clientCustomId", res.data.client.clientId);
                localStorage.setItem("clientName", res.data.client.name);
                localStorage.setItem("clientEmail", res.data.client.email);
                localStorage.setItem("clientMobile", res.data.client.mobile);
                localStorage.setItem("companyName", res.data.client.companyName);
                localStorage.setItem("employeesCount", res.data.client.employeesCount);
                localStorage.setItem("accessibleProducts", JSON.stringify(res.data.client.accessibleProducts));
                localStorage.setItem("userRole", "client");
                localStorage.setItem("clientData", JSON.stringify(res.data.client));

                showToastWithCleanup("✅ Auto-login successful! Redirecting...", 'success');
                setLoading(false);
                
                setTimeout(() => {
                    navigateWithCleanup("/recruitment-dashboard");
                }, 1000);
            } else {
                setShowOtpScreen(true);
                showToastWithCleanup("⚠️ Please enter the OTP sent to your email to complete login.", 'error');
                setLoading(false);
            }
        } catch (err) {
            setShowOtpScreen(true);
            showToastWithCleanup("⚠️ Please enter the OTP sent to your email to complete login.", 'error');
            setLoading(false);
        }
    };

    // ─── CHECK URL PARAMS FOR AUTO-LOGIN ───
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const email = urlParams.get('email');
        const password = urlParams.get('password');
        const autoLogin = urlParams.get('autoLogin');
        const clientLogin = urlParams.get('clientLogin');
        const skipOtpParam = urlParams.get('skipOtp');
        
        console.log('🔍 URL Params:', { email, password, autoLogin, clientLogin, skipOtp: skipOtpParam });

        if (autoLogin === 'true' && clientLogin === 'true' && email && password) {
            console.log('✅ Client auto-login params found!');
            showToastWithCleanup("🔐 Auto-login in progress...", 'success');
            handleAutoLogin(email, password, skipOtpParam);
        }
    }, [location]);

    // ─── HANDLERS ───
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 6) {
            setOtpData({ ...otpData, otp: value });
        }
    };

    // ─── ACCESS LOGIN HANDLER ───
    const handleAccessLogin = async (e) => {
        e.preventDefault();
        
        clearAllToasts();
        setMessage({ text: "", type: "" });
        setLoading(true);

        try {
            // Using the access-login API endpoint
            const res = await axios.post(`https://ingrainhirebackend.ingrainsystems.com/api/candidate/access-login`, {
                email: formData.email,
                password: formData.password
            });
            
            console.log("Access Login response:", res.data);
            
            if (res.data.success) {
                // Extract data from response
                const { employee, client, loginType } = res.data;
                
                // ⭐ IMPORTANT: Get clientId from employee object (since client is null)
                const clientId = employee?.clientId || "";
                
                // Store employee access data in localStorage
                localStorage.setItem("accessToken", res.data.token || "access-token");
                localStorage.setItem("userRole", "employee");
                localStorage.setItem("loginType", loginType || "employee");
                
                // Store employee data
                localStorage.setItem("employeeData", JSON.stringify(employee || {}));
                localStorage.setItem("employeeId", employee?._id || "");
                localStorage.setItem("employeeName", employee?.employeeName || "");
                localStorage.setItem("employeeEmail", employee?.employeeEmail || formData.email);
                localStorage.setItem("employeeRole", employee?.employeeRole || "");
                localStorage.setItem("employeeDepartment", employee?.employeeDepartment || "");
                localStorage.setItem("employeeProfileImage", employee?.employeeProfileImage || "");
                localStorage.setItem("employeePages", JSON.stringify(employee?.pages || []));
                
                // ⭐ Store clientId from employee (this is the client this employee belongs to)
                localStorage.setItem("clientId", clientId);
                
                // Store client data if available (but in your response it's null)
                if (client) {
                    localStorage.setItem("clientData", JSON.stringify(client));
                    localStorage.setItem("clientName", client?.name || "");
                    localStorage.setItem("clientEmail", client?.email || "");
                    localStorage.setItem("companyName", client?.companyName || "");
                    localStorage.setItem("accessibleProducts", JSON.stringify(client?.accessibleProducts || []));
                } else {
                    // If client is null, we can store clientId from employee
                    // You might want to fetch client details separately if needed
                    localStorage.setItem("clientData", JSON.stringify({ _id: clientId }));
                }

                showToastWithCleanup("✅ Access login successful! Redirecting...", 'success');
                setMessage({ text: "Login successful! Redirecting to dashboard...", type: "success" });
                
                setTimeout(() => {
                    navigateWithCleanup("/recruitment-dashboard");
                }, 1500);
            } else {
                const errorMsg = res.data.message || "Access login failed. Please try again.";
                showToastWithCleanup(errorMsg, 'error');
                setMessage({ text: errorMsg, type: "error" });
            }
        } catch (err) {
            console.error("Access Login error:", err);
            const errorMsg = err.response?.data?.message || "Invalid credentials. Please try again.";
            showToastWithCleanup(errorMsg, 'error');
            setMessage({ text: errorMsg, type: "error" });
        } finally {
            setLoading(false);
        }
    };

    // ─── CLIENT LOGIN HANDLER ───
    const handleClientLogin = async (e) => {
        e.preventDefault();
        
        clearAllToasts();
        setMessage({ text: "", type: "" });
        setLoading(true);

        try {
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/clientlogin`, formData);
            
            console.log("Client Login response:", res.data);
            
            if (res.data.success) {
                setOtpData({
                    otp: "",
                    clientId: res.data.clientId || "",
                    email: res.data.email || formData.email
                });
                
                showToastWithCleanup("OTP sent to your email! Please verify to continue.", 'success');
                setShowOtpScreen(true);
            } else {
                const errorMsg = res.data.message || "Login failed. Please try again.";
                showToastWithCleanup(errorMsg, 'error');
                setMessage({ text: errorMsg, type: "error" });
            }
        } catch (err) {
            console.error("Client Login error:", err);
            const errorMsg = err.response?.data?.message || "Invalid credentials. Please try again.";
            showToastWithCleanup(errorMsg, 'error');
            setMessage({ text: errorMsg, type: "error" });
        } finally {
            setLoading(false);
        }
    };

    // ─── MAIN SUBMIT HANDLER ───
    const handleSubmit = (e) => {
        e.preventDefault();
        if (loginMode === 'client') {
            handleClientLogin(e);
        } else {
            handleAccessLogin(e);
        }
    };

    const handleOtpVerify = async (e) => {
        e.preventDefault();
        
        clearAllToasts();
        setOtpLoading(true);
        setMessage({ text: "", type: "" });

        try {
            const payload = {
                otp: otpData.otp
            };
            
            if (otpData.clientId) {
                payload.clientId = otpData.clientId;
            } else {
                payload.email = otpData.email;
            }
            
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/verify-client-otp`, payload);
            
            console.log("OTP verification response:", res.data);
            
            if (res.data.success && res.data.token && res.data.client) {
                clearAllToasts();
                
                localStorage.setItem("clientToken", res.data.token);
                localStorage.setItem("clientId", res.data.client._id);
                localStorage.setItem("clientCustomId", res.data.client.clientId);
                localStorage.setItem("clientName", res.data.client.name);
                localStorage.setItem("clientEmail", res.data.client.email);
                localStorage.setItem("clientMobile", res.data.client.mobile);
                localStorage.setItem("companyName", res.data.client.companyName);
                localStorage.setItem("employeesCount", res.data.client.employeesCount);
                localStorage.setItem("accessibleProducts", JSON.stringify(res.data.client.accessibleProducts));
                localStorage.setItem("userRole", "client");
                localStorage.setItem("clientData", JSON.stringify(res.data.client));

                showToastWithCleanup("Login successful! Redirecting to dashboard...", 'success');
                setMessage({ text: "Login successful! Redirecting to dashboard...", type: "success" });
                
                setTimeout(() => {
                    navigateWithCleanup("/recruitment-dashboard");
                }, 1500);
            } else {
                const errorMsg = res.data.message || "Invalid OTP. Please try again.";
                showToastWithCleanup(errorMsg, 'error');
                setMessage({ text: errorMsg, type: "error" });
            }
        } catch (err) {
            console.error("OTP verification error:", err);
            const errorMsg = err.response?.data?.message || "OTP verification failed. Please try again.";
            showToastWithCleanup(errorMsg, 'error');
            setMessage({ text: errorMsg, type: "error" });
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendOtp = async () => {
        clearAllToasts();
        setLoading(true);
        setMessage({ text: "", type: "" });
        
        try {
            const res = await axios.post(`https://api.ingrainsystems.com/api/clients/clientlogin`, {
                email: formData.email,
                password: formData.password
            });
            
            if (res.data.success) {
                showToastWithCleanup("New OTP sent to your email!", 'success');
                setOtpData({ ...otpData, otp: "" });
            } else {
                const errorMsg = res.data.message || "Failed to resend OTP.";
                showToastWithCleanup(errorMsg, 'error');
                setMessage({ text: errorMsg, type: "error" });
            }
        } catch (err) {
            console.error("Resend OTP error:", err);
            const errorMsg = err.response?.data?.message || "Failed to resend OTP. Please try again.";
            showToastWithCleanup(errorMsg, 'error');
            setMessage({ text: errorMsg, type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        clearAllToasts();
        setShowOtpScreen(false);
        setOtpData({ otp: "", clientId: "", email: "" });
        setMessage({ text: "", type: "" });
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 font-sans">
            <div className="w-full max-w-5xl bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col md:flex-row border border-gray-200">

                {/* Visual Side */}
                <div className="md:w-1/2 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 p-12 text-white flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1)_0%,transparent_60%)]"></div>

                    <div className="relative z-10">
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-10 backdrop-blur-sm border border-white/20 shadow-sm">
                            <FaBuilding className="text-2xl" />
                        </div>
                        <h2 className="text-4xl font-black leading-tight mb-4">
                            {showOtpScreen ? "Verify Your" : "Welcome back,"} <br />
                            <span className="text-indigo-200">
                                {showOtpScreen ? "Identity" : "Business Partner"}
                            </span>
                        </h2>
                        <p className="text-lg text-indigo-100/80 font-medium max-w-xs leading-relaxed">
                            {showOtpScreen 
                                ? "Enter the 6-digit code sent to your email to complete login." 
                                : "Access your client dashboard to manage job postings and track candidates."}
                        </p>
                    </div>

                    <div className="relative z-10 pt-12">
                        <div className="flex -space-x-3 mb-6">
                            {[21, 22, 23, 24].map(i => (
                                <div key={i} className="w-10 h-10 rounded-full border-2 border-indigo-600 bg-indigo-500 flex items-center justify-center overflow-hidden">
                                    <img src={`https://i.pravatar.cc/100?img=${i}`} alt="user" className="w-full h-full object-cover" />
                                </div>
                            ))}
                            <div className="w-10 h-10 rounded-full border-2 border-indigo-600 bg-indigo-700 flex items-center justify-center text-[10px] font-black">
                                +500
                            </div>
                        </div>
                        <p className="text-[10px] font-black text-indigo-200/60 uppercase tracking-[0.2em]">
                            Trusted by 500+ businesses
                        </p>
                    </div>
                </div>

                {/* Form Side */}
                <div className="md:w-1/2 p-12 md:p-20 flex flex-col justify-center bg-white">
                    {!showOtpScreen ? (
                        // Login Form
                        <>
                            <div className="mb-8">
                                <h1 className="text-2xl font-black text-gray-900 mb-1">Client Login</h1>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                    Access your business account
                                </p>
                            </div>

                            {/* Login Mode Toggle */}
                            <div className="mb-6 flex bg-gray-100 rounded-xl p-1 border border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLoginMode('client');
                                        setMessage({ text: "", type: "" });
                                        clearAllToasts();
                                    }}
                                    className={`flex-1 py-2.5 px-4 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                                        loginMode === 'client'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Client Login
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLoginMode('access');
                                        setMessage({ text: "", type: "" });
                                        clearAllToasts();
                                    }}
                                    className={`flex-1 py-2.5 px-4 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                                        loginMode === 'access'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Employee Access
                                </button>
                            </div>

                            {message.text && (
                                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${
                                    message.type === "success"
                                        ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                        : "bg-rose-50 text-rose-600 border-rose-100"
                                    }`}>
                                    {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                                    <span className="text-sm font-bold">{message.text}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Email Address</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 transition-colors">
                                            <FiMail className="text-lg" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-11 pr-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm"
                                            placeholder={loginMode === 'client' ? "company@example.com" : "employee@company.com"}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Password</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 transition-colors">
                                            <FiLock className="text-lg" />
                                        </div>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-11 pr-12 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-indigo-600 transition-colors"
                                        >
                                            {showPassword ? <FiEyeOff className="text-lg" /> : <FiEye className="text-lg" />}
                                        </button>
                                    </div>
                                    {loginMode === 'access' && (
                                        <p className="text-[10px] text-gray-400 mt-1 ml-1 font-medium">
                                            Default password: 123456
                                        </p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !formData.email || !formData.password}
                                    className="w-full py-5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-black rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group uppercase tracking-widest text-sm"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>{loginMode === 'client' ? 'Sign In' : 'Access Login'}</span>
                                            <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            <p className="mt-8 text-center text-[11px] text-gray-500 font-bold uppercase tracking-wider">
                                {loginMode === 'client' ? (
                                    <>
                                        Don't have a client account?{" "}
                                        <Link to="/client-register" className="text-indigo-600 font-black hover:underline underline-offset-8 transition-all">
                                            Create Account
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        Need client access?{" "}
                                        <Link to="/client-register" className="text-indigo-600 font-black hover:underline underline-offset-8 transition-all">
                                            Register as Client
                                        </Link>
                                    </>
                                )}
                            </p>
                        </>
                    ) : (
                        // OTP Verification Form
                        <>
                            <div className="mb-8">
                                <button
                                    onClick={handleBackToLogin}
                                    className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:underline mb-6 flex items-center gap-2 transition-all"
                                >
                                    ← Back to Login
                                </button>
                                <h1 className="text-2xl font-black text-gray-900 mb-1">Verify OTP</h1>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                    Enter verification code
                                </p>
                            </div>

                            {message.text && (
                                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${
                                    message.type === "success"
                                        ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                        : "bg-rose-50 text-rose-600 border-rose-100"
                                    }`}>
                                    {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                                    <span className="text-sm font-bold">{message.text}</span>
                                </div>
                            )}

                            <form onSubmit={handleOtpVerify} className="space-y-5">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Enter OTP</label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            name="otp"
                                            value={otpData.otp}
                                            onChange={handleOtpChange}
                                            required
                                            maxLength="6"
                                            className="w-full px-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm text-center text-2xl tracking-[0.5em]"
                                            placeholder="000000"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        A 6-digit OTP has been sent to {otpData.email || formData.email}
                                    </p>
                                </div>

                                <button
                                    type="submit"
                                    disabled={otpLoading || !otpData.otp || otpData.otp.length !== 6}
                                    className="w-full py-5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-black rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group uppercase tracking-widest text-sm"
                                >
                                    {otpLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>Verify OTP</span>
                                            <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={loading}
                                        className="text-xs text-indigo-600 font-bold hover:underline disabled:opacity-50 transition-all"
                                    >
                                        {loading ? "Sending..." : "Resend OTP"}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientLogin;